// LoginForm.js
// Public user login form for job portal

import React, { useState, useEffect } from 'react';
import { useGlobalData } from '../context/GlobalDataContext';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { loginPublicUser } from '../api/api';
import './LoginForm.css';


const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setProfile } = useGlobalData();
  const [loginWith, setLoginWith] = useState('email'); // 'email' or 'mobile'
  const [form, setForm] = useState({
    email: '',
    mobile: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNotif, setShowNotif] = useState(false);
  const [showErrorNotif, setShowErrorNotif] = useState(false);

  useEffect(() => {
    const popupTypeFromState = location.state?.popupType;
    const popupMessageFromState = location.state?.popupMessage;

    if (popupTypeFromState === 'success' && popupMessageFromState) {
      setSuccess(popupMessageFromState);
      setShowNotif(true);
      setShowErrorNotif(false);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Sync with dark mode from Navbar
  useEffect(() => {
    const syncTheme = () => {
      if (document.body.classList.contains('dark-mode')) {
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        document.documentElement.setAttribute('data-theme', 'light');
      }
    };
    syncTheme();
    window.addEventListener('darkModeChanged', syncTheme);
    return () => window.removeEventListener('darkModeChanged', syncTheme);
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLoginWithChange = (val) => {
    setLoginWith(val);
    setForm(f => ({ ...f, email: '', mobile: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const payload = {
        password: form.password,
        role: 'user'
      };
      if (loginWith === 'email') payload.email = form.email;
      if (loginWith === 'mobile') payload.mobile = form.mobile;
      const res = await loginPublicUser(payload);
      setSuccess(res.data.message || 'Login successful!');
      setShowNotif(true);
      setTimeout(() => setShowNotif(false), 3500);
      // Store login identity for profile update
      if (res.data.user) {
        if (res.data.user.email) {
          localStorage.setItem('userEmail', res.data.user.email);
        } else {
          localStorage.removeItem('userEmail');
        }
        if (res.data.user.mobile) {
          localStorage.setItem('userMobile', res.data.user.mobile);
        } else {
          localStorage.removeItem('userMobile');
        }
      }
      // Store JWT token in localStorage for authenticated actions
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        // Dispatch custom event for same-tab listeners (Navbar)
        window.dispatchEvent(new Event('tokenChanged'));
      }
      // Fetch and set profile after login
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        const token = res.data.token || localStorage.getItem('token');
        if (token) {
          const profileRes = await axios.get(`${apiUrl}/publicuser/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const user = profileRes.data.user;
          setProfile(prev => ({ ...prev, ...user }));
        }
        // Ensure notifications refresh after profile is set
        setTimeout(() => {
          window.dispatchEvent(new Event('dataChanged'));
        }, 0);
      } catch (e) {}
      setTimeout(() => {
        navigate('/publicuser/publicpage');
      }, 1000);
    } catch (err) {
      let msg = err.response?.data?.error || 'Email/Mobile No. or Password is wrong';
      if (msg.toLowerCase().includes('invalid credential') || msg.toLowerCase().includes('invalid login')) {
        msg = 'Email/Mobile No. or Password is wrong';
      }
      setError(msg);
      setShowErrorNotif(true);
      setTimeout(() => setShowErrorNotif(false), 3500);
    }
  };

  return (
    <div className="public-login-form-container">
      {showNotif && (
        <div className="registration-success-toast">
          {success}
        </div>
      )}
      {showErrorNotif && (
        <div className="login-error-toast">
          {error}
        </div>
      )}
      <form className="public-login-form" onSubmit={handleSubmit} autoComplete="off">
        <h2>Login</h2>
        <div className="public-register-toggle-group" style={{marginBottom: '1.2rem'}}>
          <span className="public-register-toggle-label">Login with:</span>
          <button
            type="button"
            className={`public-register-toggle-btn${loginWith === 'email' ? ' selected' : ''}`}
            onClick={() => handleLoginWithChange('email')}
            tabIndex={0}
          >
            Email
          </button>
          <button
            type="button"
            className={`public-register-toggle-btn${loginWith === 'mobile' ? ' selected' : ''}`}
            onClick={() => handleLoginWithChange('mobile')}
            tabIndex={0}
          >
            Mobile
          </button>
        </div>
        {loginWith === 'email' && (
          <input name="email" type="email" placeholder="Email" value={form.email} onChange={handleChange} required />
        )}
        {loginWith === 'mobile' && (
          <input name="mobile" type="tel" placeholder="Mobile Number" value={form.mobile} onChange={handleChange} pattern="[0-9]{10}" maxLength={10} minLength={10} required />
        )}
        <input name="password" type="password" placeholder="Password" value={form.password} onChange={handleChange} required />
        <button type="submit">Login</button>
        {error && <div className="public-error-message">{error}</div>}
        {success && <div className="public-success-message">{success}</div>}
        <div className="public-form-link-container">
          <span>Don't have an account?{' '}
            <Link to="/publicuser/register" className="public-form-link">Register here</Link>
          </span>
        </div>
        <div className="public-form-link-container">
          <span>Forgot your password?{' '}
            <Link to="/publicuser/password-reset" className="public-form-link">Reset here</Link>
          </span>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
